import { type RefObject, useEffect } from "react";

import type { ExtendedUIMessage } from "../chat.types";
import { selectMessagesAfterEviction } from "../ephemeral-slot";
import type { AiSdkChatStatus } from "./action-for-stream-state-event";

/**
 * Evicts settled turns from `useChat.messages` on the server-authoritative render path (B-1445 S3).
 *
 * There `useChat.messages` is a transient live-turn buffer, not the render source — the render comes
 * from `serverHistory` plus the ephemeral slot. Once a turn is durably in `serverHistory` it can
 * leave the buffer; `renderKey` identity (S2) makes that gap-free (the evicted live turn and its
 * persisted row share a render key, so the render list is unchanged).
 *
 * Why it matters: `ai@6`'s `createStreamingUIMessageState` seeds a resumed stream by CLONING the
 * last message iff its role is `assistant`. If a settled elicit turn is still the tail when the user
 * submits the elicitation, the resume continuation seed clones its `tool-elicit` part — whose shared
 * toolCallId then makes render-dedupe drop the continuation (the S1-without-S3 regression). Evicting
 * the elicit turn while the session sits idle in `AWAITING_USER_INPUT` leaves a non-assistant tail,
 * so the continuation seeds fresh and clean.
 *
 * Gating: the SDK idle (never mutate the buffer mid-stream) and no elicit submit in flight.
 * Deliberately NOT gated on session-settled — the elicit-resume clean-seed
 * must happen during `AWAITING_USER_INPUT`, which is idle but not terminal. `selectMessagesAfterEviction`
 * returns `null` when nothing is evictable, so a stable buffer never churns `setMessages`.
 *
 * Ordering: the clean seed depends on this effect having run before the user submits the elicit.
 * That holds in the browser event model — the elicit turn settles (persisted, SDK idle) on a commit
 * whose passive effect flushes before any later discrete input (the user's Confirm/Skip click is a
 * separate task), so the settled turn is already evicted by submit time. The residual is a fast
 * pre-settlement submit; renderKey identity (S2) still collapses the continuation at settlement even
 * if that seed cloned, so the worst case is a transient, not a stuck duplicate.
 */
export function useEvictSettledTurns(params: {
  status: AiSdkChatStatus;
  messages: ExtendedUIMessage[];
  serverHistory: ExtendedUIMessage[] | undefined;
  setMessages: (messages: ExtendedUIMessage[]) => void;
  isElicitationSubmittingRef: RefObject<boolean>;
}): void {
  const { status, messages, serverHistory, setMessages, isElicitationSubmittingRef } = params;

  useEffect(() => {
    if (!serverHistory || status === "streaming" || status === "submitted") {
      return;
    }
    if (isElicitationSubmittingRef.current) {
      return;
    }
    const retained = selectMessagesAfterEviction(messages, serverHistory);
    if (retained) {
      setMessages(retained);
    }
    // isElicitationSubmittingRef is a ref (identity-stable), read not subscribed.
  }, [status, messages, serverHistory, setMessages, isElicitationSubmittingRef]);
}
